import type {
  GraphNode, GraphEdge, CruxItem, DiffResult, StrainGraph, ID,
} from "@/types";
import { CRUX_CATEGORIES } from "@/lib/vocabulary";

// ---------------------------------------------------------------- adjacency
// upstream: nodes (or edges) pointing INTO this node
// downstream: nodes reachable by following outgoing support/define/qualify/evidence edges
// undercuts are special: they target edges, not nodes.

export function incomingNodes(nodeId: ID, edges: GraphEdge[]): GraphEdge[] {
  return edges.filter((e) => e.target === nodeId && e.relation !== "undercuts");
}

export function outgoingNodes(nodeId: ID, edges: GraphEdge[]): GraphEdge[] {
  return edges.filter((e) => e.source === nodeId && e.target);
}

export function undercutsOf(edgeId: ID, edges: GraphEdge[]): GraphEdge[] {
  return edges.filter((e) => e.relation === "undercuts" && e.target_edge === edgeId);
}

export function downstreamNodes(nodeId: ID, nodes: GraphNode[], edges: GraphEdge[]): GraphNode[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<ID>();
  const out: GraphNode[] = [];
  const stack: ID[] = [nodeId];
  while (stack.length) {
    const cur = stack.pop()!;
    if (visited.has(cur)) continue;
    visited.add(cur);
    const outs = outgoingNodes(cur, edges);
    for (const e of outs) {
      if (!e.target) continue;
      if (visited.has(e.target)) continue;
      const n = nodeMap.get(e.target);
      if (n) {
        out.push(n);
        stack.push(e.target);
      }
    }
  }
  return out;
}

export function upstreamNodes(nodeId: ID, nodes: GraphNode[], edges: GraphEdge[]): GraphNode[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<ID>();
  const out: GraphNode[] = [];
  const stack: ID[] = [nodeId];
  while (stack.length) {
    const cur = stack.pop()!;
    if (visited.has(cur)) continue;
    visited.add(cur);
    const ins = incomingNodes(cur, edges);
    for (const e of ins) {
      if (visited.has(e.source)) continue;
      const n = nodeMap.get(e.source);
      if (n) {
        out.push(n);
        stack.push(e.source);
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------- crux scoring
// §9.7 heuristic — prototype values are precomputed in seed data, but we
// recompute here so newly-created nodes get scored too.
//
// crux_score =
//   downstream_node_count
//   + 2 * undercut_count
//   + 1.5 * fork_divergence_count
//   + 1.5 * high_disagreement_flag
//   + 2 * belief_change_count
//   + evidence_dispute_count

export function computeCruxScore(node: GraphNode, nodes: GraphNode[], edges: GraphEdge[]): number {
  const downstream = downstreamNodes(node.id, nodes, edges).length;

  // undercuts targeting edges that touch this node, OR edges from this node
  const touchingEdges = edges.filter((e) => e.source === node.id || e.target === node.id);
  const undercuts = touchingEdges.reduce((acc, e) => acc + undercutsOf(e.id, edges).length, 0);
  // also count undercuts whose source IS this node (this node is doing the undercutting — still a crux signal)
  const asUndercutter = edges.filter((e) => e.source === node.id && e.relation === "undercuts").length;

  const forks = node.stats?.forks ?? 0;
  const disagreement = (node.stats?.contention ?? 0) > 45 ? 1 : 0;
  const beliefChange = node.stats?.mind_changes ?? 0;
  const evidenceDisputes = node.stats?.evidence ? Math.min(2, Math.floor(node.stats.evidence / 2)) : 0;

  return (
    downstream +
    2 * (undercuts + asUndercutter) +
    1.5 * forks +
    1.5 * disagreement +
    2 * beliefChange +
    evidenceDisputes
  );
}

export function computeCruxes(graph: StrainGraph): CruxItem[] {
  const { nodes, edges } = graph;
  const items: CruxItem[] = nodes
    .filter((n) => n.badges?.includes("thesis") === false) // thesis is its own thing
    .map((node) => {
      const score = node.crux?.score ?? computeCruxScore(node, nodes, edges);
      const downstream = downstreamNodes(node.id, nodes, edges).length;
      const touchingEdges = edges.filter((e) => e.source === node.id || e.target === node.id);
      const undercuts = touchingEdges.reduce((acc, e) => acc + undercutsOf(e.id, edges).length, 0);
      return {
        node_id: node.id,
        node_text: node.text,
        score,
        category: node.crux?.category ?? "load_bearing_assumption",
        reason: node.crux?.reason ?? inferCruxReason(node, downstream, undercuts, edges),
        affected_downstream: downstream,
        forks_diverging: node.stats?.forks ?? 0,
        undercuts,
        stance_split: {
          agree: Math.round(node.stats?.agree ?? 0),
          disagree: Math.round(node.stats?.disagree ?? 0),
          unsure: Math.round(node.stats?.unsure ?? 0),
        },
        belief_change_count: node.stats?.mind_changes ?? 0,
      };
    })
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score);
  return items;
}

function inferCruxReason(node: GraphNode, downstream: number, undercuts: number, edges: GraphEdge[]): string {
  const parts: string[] = [];
  if (downstream > 0) parts.push(`${downstream} downstream claim${downstream === 1 ? "" : "s"} depend on it`);
  if (undercuts > 0) parts.push(`${undercuts} undercut${undercuts === 1 ? "" : "s"} target its inferences`);
  if ((node.stats?.forks ?? 0) > 0) parts.push(`${node.stats?.forks} fork${node.stats?.forks === 1 ? "" : "s"} changed it`);
  if ((node.stats?.mind_changes ?? 0) > 0) parts.push(`${node.stats?.mind_changes} mind-change${node.stats?.mind_changes === 1 ? "" : "s"} reported here`);
  if (parts.length === 0) return "Marked as a point of contention.";
  return `This is probably a crux because ${parts.join(", ")}.`;
}

// ---------------------------------------------------------------- diffing
// MVP: match by stable id (§10.6). later we can add similarity matching.

export function computeDiff(left: StrainGraph, right: StrainGraph): DiffResult {
  const leftNodes = new Map(left.nodes.map((n) => [n.id, n]));
  const rightNodes = new Map(right.nodes.map((n) => [n.id, n]));
  const leftEdges = new Map(left.edges.map((e) => [e.id, e]));
  const rightEdges = new Map(right.edges.map((e) => [e.id, e]));

  const added_nodes: GraphNode[] = [];
  const removed_nodes: GraphNode[] = [];
  const edited_nodes: { left: GraphNode; right: GraphNode; field: string }[] = [];

  for (const [id, rn] of rightNodes) {
    const ln = leftNodes.get(id);
    if (!ln) added_nodes.push(rn);
    else if (ln.text !== rn.text || ln.type !== rn.type || ln.status !== rn.status) {
      const fields: string[] = [];
      if (ln.text !== rn.text) fields.push("wording");
      if (ln.type !== rn.type) fields.push("type");
      if (ln.status !== rn.status) fields.push("status");
      edited_nodes.push({ left: ln, right: rn, field: fields.join(", ") });
    }
  }
  for (const [id, ln] of leftNodes) {
    if (!rightNodes.has(id)) removed_nodes.push(ln);
  }

  const added_edges: GraphEdge[] = [];
  const removed_edges: GraphEdge[] = [];
  const type_changed_edges: { left: GraphEdge; right: GraphEdge }[] = [];

  for (const [id, re] of rightEdges) {
    const le = leftEdges.get(id);
    if (!le) added_edges.push(re);
    else if (le.relation !== re.relation || le.label !== re.label) {
      if (le.relation !== re.relation) type_changed_edges.push({ left: le, right: re });
    }
  }
  for (const [id, le] of leftEdges) {
    if (!rightEdges.has(id)) removed_edges.push(le);
  }

  const summary = buildDiffSummary(
    added_nodes.length, removed_nodes.length, edited_nodes.length,
    added_edges.length, removed_edges.length, type_changed_edges.length,
    right.version.fork_intent,
  );

  return {
    left_version: left.version.id,
    right_version: right.version.id,
    added_nodes, removed_nodes, edited_nodes,
    added_edges, removed_edges, type_changed_edges,
    summary,
  };
}

function buildDiffSummary(
  addedN: number, removedN: number, editedN: number,
  addedE: number, removedE: number, changedE: number,
  intent: string | null,
): string {
  const parts: string[] = [];
  if (addedN) parts.push(`+${addedN} node${addedN === 1 ? "" : "s"}`);
  if (removedN) parts.push(`-${removedN} node${removedN === 1 ? "" : "s"}`);
  if (editedN) parts.push(`~${editedN} edited`);
  if (addedE) parts.push(`+${addedE} edge${addedE === 1 ? "" : "s"}`);
  if (removedE) parts.push(`-${removedE} edge${removedE === 1 ? "" : "s"}`);
  if (changedE) parts.push(`~${changedE} relation changed`);
  const intentLabel = intent && intent !== "null" ? `${intent} fork — ` : "";
  return `${intentLabel}${parts.join(", ") || "no structural changes"}`;
}

// ---------------------------------------------------------------- stance summary
export function stanceSummary(graph: StrainGraph) {
  // aggregate node agreement into a strain-level before/after proxy;
  // only nodes with real stance data participate
  const nodes = graph.nodes.filter((n) => typeof n.stats?.agree === "number");
  if (nodes.length === 0) return { before: 50, after: 50, moved: 0, movedBy: undefined as ID | undefined };
  const avg = nodes.reduce((a, n) => a + (n.stats?.agree ?? 0), 0) / nodes.length;
  return {
    before: 50,
    after: Math.round(avg),
    moved: Math.round(avg - 50),
    movedBy: graph.nodes.find((n) => n.badges?.includes("crux"))?.id,
  };
}
